import apiSlice from 'renderer/shared/state/apiSlice';
import {
  AdminDeactivateAccountRequest,
  AdminDeactivateAccountResponse,
  AdminDeactivateAccountResponseSchema,
  AdminUpdatePasswordRequest,
  AdminUpdatePasswordResponse,
  AdminUpdatePasswordResponseSchema,
  AdminVerifyAccountRequest,
  AdminVerifyAccountResponse,
  AdminVerifyAccountResponseSchema,
  GetUserDetailsRequest,
  GetUserDetailsResponse,
  GetUserDetailsResponseSchema,
  ListUsersRequest,
  ListUsersResponse,
  ListUsersResponseSchema,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
  UpdateSettingsResponseSchema,
  UpdateUserSecurityRequest,
  UpdateUserSecurityResponse,
  UpdateUserSecurityResponseSchema,
} from '../schemas/users.schemas';
import {
  AddNoteRequest,
  AddNoteResponse,
  AddNoteResponseSchema,
  GetNotesRequest,
  GetNotesResponse,
  NotesSchema,
  UpdateNoteRequest,
  UpdateNoteResponse,
  UpdateNoteResponseSchema,
} from '../schemas/userDetails.schema';
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
} from '../schemas/createSubscription.schema';

const extendedApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Cancel subscription
    createSubscription: builder.mutation<
      CreateSubscriptionResponse,
      CreateSubscriptionRequest
    >({
      query: ({
        userId,
        productId,
        platform,
        hostApplication,
        // numberOfCopies,
        discountCode,
        startDate,
        paymentMethodId,
        overrideStripePriceId,
      }) => ({
        url: `/subscription/`,
        method: 'POST',
        body: {
          userId,
          productId,
          platform,
          hostApplication,
          // numberOfCopies,
          discountCode,
          startDate,
          paymentMethodId,
          overrideStripePriceId,
        },
      }),
      invalidatesTags: ['User'],
    }),

    // Update License Usability
    updateLicenseUsable: builder.mutation({
      query: ({ serial, status }) => ({
        url: `/subscription/${serial}/usable`,
        method: 'POST',
        body: { status },
      }),
    }),
    // Preview subscription change
    previewSubscriptionChange: builder.query({
      query: ({ subscriptionId, action, newPlanId }) => ({
        url: `/subscription/${subscriptionId}/preview`,
        method: 'POST',
        body: { action, newPlanId },
      }),
      providesTags: ['User'],
    }),
    // Upgrade subscription
    upgradeSubscription: builder.mutation({
      query: ({ subscriptionId, newPlanId, hostApplication }) => ({
        url: `/subscription/${subscriptionId}/upgrade`,
        method: 'PATCH',
        body: { newPlanId, hostApplication },
      }),
      invalidatesTags: ['User'],
    }),

    // Downgrade subscription
    downgradeSubscription: builder.mutation({
      query: ({ subscriptionId, newPlanId, hostApplication }) => ({
        url: `/subscription/${subscriptionId}/downgrade`,
        method: 'PATCH',
        body: { lowerPriceId: newPlanId, hostApplication },
      }),
      invalidatesTags: ['User'],
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation({
      query: (subscriptionId) => ({
        url: `/subscription/${subscriptionId}/cancel`,
        method: 'PATCH',
      }),
      invalidatesTags: ['User'],
    }),

    // Release subscription schedule
    releaseSchedule: builder.mutation({
      query: (subscriptionId) => ({
        url: `/subscription/${subscriptionId}/releaseSchedule`,
        method: 'PATCH',
      }),
      invalidatesTags: ['User'],
    }),

    // Get all users
    listUsers: builder.query<ListUsersResponse, ListUsersRequest>({
      query: ({ page, limit, search, sortBy, sortOrder, filter }) => {
        // Construct the base query parameters
        let queryParams = `page=${page}&limit=${limit}`;

        // Append the search parameter if it's present
        if (search) queryParams += `&search=${encodeURIComponent(search)}`;

        // Append the sortBy and sortOrder parameters if they're present
        if (sortBy) queryParams += `&sortBy=${encodeURIComponent(sortBy)}`;
        if (sortOrder)
          queryParams += `&sortOrder=${encodeURIComponent(sortOrder)}`;

        // Append filter parameters if they're present
        // Assuming 'filter' is an object with keys corresponding to filterable fields
        if (filter) {
          Object.keys(filter).forEach((key) => {
            if (filter[key] !== undefined) {
              queryParams += `&${encodeURIComponent(key)}=${encodeURIComponent(
                filter[key],
              )}`;
            }
          });
        }

        // Return the constructed request
        return { url: `/user?${queryParams}`, method: 'GET' };
      },
      providesTags: ['User'],
      extraOptions: {
        responseSchema: ListUsersResponseSchema,
      },
    }),
    // Get user details
    getUserDetails: builder.query<
      GetUserDetailsResponse,
      GetUserDetailsRequest
    >({
      query: (id) => `/user/details/${id}`,
      providesTags: ['User', 'PaymentMethods', 'Notification'],
      extraOptions: {
        responseSchema: GetUserDetailsResponseSchema,
      },
    }),
    // Update user profile
    editUserProfile: builder.mutation<
      UpdateSettingsResponse,
      UpdateSettingsRequest
    >({
      query: (updateRequest) => ({
        url: `/user/profile/${updateRequest.userId}`,
        method: 'PATCH',
        body: updateRequest,
      }),
      invalidatesTags: ['User'],
      extraOptions: {
        responseSchema: UpdateSettingsResponseSchema,
      },
    }),
    // Update user security
    editUserSecurity: builder.mutation<
      UpdateUserSecurityResponse,
      UpdateUserSecurityRequest
    >({
      query: (updateRequest) => ({
        url: `/user/profile/${updateRequest.userId}/security`,
        method: 'PATCH',
        body: updateRequest,
      }),
      extraOptions: {
        responseSchema: UpdateUserSecurityResponseSchema,
      },
    }),
    // Admin update password
    adminUpdatePassword: builder.mutation<
      AdminUpdatePasswordResponse,
      AdminUpdatePasswordRequest
    >({
      query: (updateRequest) => ({
        url: `/user/profile/${updateRequest.userId}/admin-change-password`,
        method: 'PATCH',
        body: { newPassword: updateRequest.newPassword },
      }),
      extraOptions: {
        responseSchema: AdminUpdatePasswordResponseSchema,
      },
    }),
    // Admin verify account
    adminVerifyAccount: builder.mutation<
      AdminVerifyAccountResponse,
      AdminVerifyAccountRequest
    >({
      query: ({ userId }) => ({
        url: `/user/profile/${userId}/admin-verify-account`,
        method: 'POST',
        body: { status: 'active', isEmailVerified: true },
      }),
      invalidatesTags: ['User'],
      extraOptions: {
        responseSchema: AdminVerifyAccountResponseSchema,
      },
    }),
    // Admin verify account
    adminDeactivateAccount: builder.mutation<
      AdminDeactivateAccountResponse,
      AdminDeactivateAccountRequest
    >({
      query: ({ userId }) => ({
        url: `/user/profile/${userId}/admin-verify-account`,
        method: 'POST',
        body: { status: 'inactive' },
      }),
      invalidatesTags: ['User'],
      extraOptions: {
        responseSchema: AdminDeactivateAccountResponseSchema,
      },
    }),
    addNote: builder.mutation<AddNoteResponse, AddNoteRequest>({
      query: ({ userId, content, hidden }) => ({
        url: `/user/${userId}/notes`,
        method: 'POST',
        body: { content, hidden },
      }),
      invalidatesTags: ['User'],
      extraOptions: {
        responseSchema: AddNoteResponseSchema,
      },
    }),
    getUserNotes: builder.query<GetNotesResponse, GetNotesRequest>({
      query: (userId) => `/user/${userId}/notes`,
      providesTags: ['User'],
      extraOptions: {
        responseSchema: NotesSchema,
      },
    }),
    updateNote: builder.mutation<UpdateNoteResponse, UpdateNoteRequest>({
      query: ({ userId, noteId, content, hidden }) => ({
        url: `/user/${userId}/notes/${noteId}`,
        method: 'PUT', // or 'PATCH' depending on your backend
        body: { content, hidden },
      }),
      invalidatesTags: ['User'],
      extraOptions: {
        responseSchema: UpdateNoteResponseSchema,
      },
    }),
    deleteNote: builder.mutation({
      query: ({ userId, noteId }) => ({
        url: `/user/${userId}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useUpdateLicenseUsableMutation,
  usePreviewSubscriptionChangeQuery,
  useCreateSubscriptionMutation,
  useUpgradeSubscriptionMutation,
  useDowngradeSubscriptionMutation,
  useCancelSubscriptionMutation,
  useReleaseScheduleMutation,
  useListUsersQuery,
  useGetUserDetailsQuery,
  useEditUserProfileMutation,
  useEditUserSecurityMutation,
  useAdminUpdatePasswordMutation,
  useAdminVerifyAccountMutation,
  useAdminDeactivateAccountMutation,
  useAddNoteMutation,
  useGetUserNotesQuery,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = extendedApi;
